import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { IconRenderer } from "../lib/icons";
import { speak } from "../lib/speech";
import Header from "../components/Header";
import UserNav from "../components/UserNav";

export default function ChoiceRunner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [choice, setChoice] = useState(null);
  const [chosen, setChosen] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/choices/${id}`);
        setChoice(data);
      } catch (e) {
        toast.error(apiError(e));
        navigate("/use");
      }
    })();
  }, [id, navigate]);

  const pick = (opt) => {
    setChosen(opt);
    speak(`I choose ${opt.label}`);
  };

  if (!choice) return <div className="min-h-screen bg-background"><Header /><p className="p-8 text-lg">Loading…</p></div>;

  return (
    <div data-testid="choice-runner" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-8 pb-32">
        <button data-testid="back-button" onClick={() => navigate("/use")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
          <ArrowLeft size={20} strokeWidth={2.5} /> Back
        </button>

        <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-center">{choice.name}</h1>

        {chosen ? (
          <div data-testid="choice-confirmation" className="mt-8 bg-[#48BB78]/15 border-2 border-[#48BB78] rounded-3xl p-8 text-center">
            <span className="mx-auto w-32 h-32 rounded-3xl bg-white grid place-items-center border-2 border-[#48BB78]">
              <IconRenderer name={chosen.icon} size={72} className="text-[#2F855A]" />
            </span>
            <p className="mt-5 text-3xl font-extrabold text-[#22543D] flex items-center justify-center gap-2">
              <Check size={32} strokeWidth={3} /> {chosen.label}
            </p>
            <button
              data-testid="choose-again-button"
              onClick={() => setChosen(null)}
              className="mt-6 min-h-[64px] px-8 rounded-full bg-white border-2 border-foreground/25 text-xl font-bold flex items-center justify-center gap-2 mx-auto hover:bg-secondary transition-colors duration-200"
            >
              <RotateCcw size={24} strokeWidth={2.5} /> Choose again
            </button>
          </div>
        ) : (
          <div data-testid="choice-options-grid" className="mt-8 grid grid-cols-2 gap-4">
            {choice.options.map((opt) => (
              <button
                key={opt.id}
                data-testid={`choice-option-${opt.id}`}
                onClick={() => pick(opt)}
                className="bg-white rounded-3xl border-2 border-[#ED8936]/60 p-5 aspect-square flex flex-col items-center justify-center gap-3 transition-transform duration-200 ease-out hover:-translate-y-0.5 active:scale-95"
              >
                <IconRenderer name={opt.icon} size={64} className="text-[#C05621]" />
                <span className="text-2xl font-extrabold leading-tight text-center">{opt.label}</span>
              </button>
            ))}
          </div>
        )}
      </main>
      <UserNav />
    </div>
  );
}
